import React from 'react';
import { Box, Typography } from '@mui/material';
import { Thermometer, Droplets, Wifi, WifiOff } from 'lucide-react';
import GlassPaper from '../../GlassPaper';
import Sparkline from './Sparkline';
import StatPill from './StatPill';
import { useRoomSensorHistory } from '../hooks/useInfraHealth';

function tempTone(t, th) {
    if (t == null) return 'muted';
    if (t >= (th?.tempCritC ?? 35)) return 'crit';
    if (t >= (th?.tempWarnC ?? 30)) return 'warn';
    return 'good';
}
function humTone(h, th) {
    if (h == null) return 'muted';
    if (h >= (th?.humidityCritPct ?? 85)) return 'crit';
    if (h >= (th?.humidityWarnPct ?? 75)) return 'warn';
    return 'good';
}
function formatSince(clock) {
    if (!clock) return '—';
    const sec = Math.max(0, Math.floor(Date.now() / 1000 - clock));
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const h = Math.floor(min / 60);
    return `${h}h ${min % 60}m ago`;
}

export default function RoomSensorCard({ room }) {
    const { series } = useRoomSensorHistory(1800);
    const avail = Boolean(room?.available) && Number(room?.availability) === 1;
    const th = room?.thresholds;
    const tVal = room?.temperature?.value;
    const hVal = room?.humidity?.value;
    const tTone = tempTone(tVal, th);
    const hTone = humTone(hVal, th);

    return (
        <GlassPaper sx={{
            p: { xs: 1.5, sm: 1.75 },
            background: 'linear-gradient(135deg, rgba(244,114,182,0.10), rgba(59,130,246,0.08) 55%, rgba(16,185,129,0.06))',
            overflow: 'hidden',
            position: 'relative',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                <Box>
                    <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.1, opacity: 0.7, fontWeight: 600 }}>
                        Room Climate — RM4
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, opacity: 0.65, mt: 0.25 }}>
                        {room?.host || 'sensor'} · {formatSince(room?.asOf)}
                    </Typography>
                </Box>
                <StatPill
                    tone={avail ? 'good' : 'crit'}
                    icon={avail ? <Wifi size={11} /> : <WifiOff size={11} />}
                >
                    {avail ? 'Online' : 'Offline'}
                </StatPill>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Reading
                    icon={<Thermometer size={14} />}
                    label="Temp"
                    value={tVal != null ? tVal.toFixed(1) : '—'}
                    unit="°C"
                    tone={tTone}
                    series={series.temperature}
                    gradient="linear-gradient(135deg, rgba(244,63,94,0.14), rgba(251,146,60,0.10))"
                    sparkColor="#fb7185"
                    sparkFill="rgba(251,113,133,0.18)"
                />
                <Reading
                    icon={<Droplets size={14} />}
                    label="Humidity"
                    value={hVal != null ? hVal.toFixed(1) : '—'}
                    unit="%"
                    tone={hTone}
                    series={series.humidity}
                    gradient="linear-gradient(135deg, rgba(56,189,248,0.14), rgba(99,102,241,0.10))"
                    sparkColor="#38bdf8"
                    sparkFill="rgba(56,189,248,0.2)"
                />
            </Box>
        </GlassPaper>
    );
}

function Reading({ icon, label, value, unit, tone, series, gradient, sparkColor, sparkFill }) {
    const toneColor = { good: '#34d399', warn: '#fbbf24', crit: '#f87171', muted: '#cbd5e1' }[tone];
    return (
        <Box sx={{
            position: 'relative',
            p: 1.25,
            borderRadius: 2,
            background: gradient,
            border: '1px solid rgba(148,163,184,0.14)',
            overflow: 'hidden',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: 0.8 }}>
                <Box sx={{ color: toneColor, display: 'flex' }}>{icon}</Box>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600 }}>{label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                <Typography sx={{
                    fontSize: { xs: 22, sm: 26 },
                    fontWeight: 700,
                    color: toneColor,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                }}>{value}</Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.7, fontWeight: 500 }}>{unit}</Typography>
            </Box>
            <Box sx={{ mt: 0.5 }}>
                <Sparkline points={series} width={240} height={24} stroke={sparkColor} fill={sparkFill} />
            </Box>
        </Box>
    );
}
